import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Clock, Car, Bike, Leaf } from 'lucide-react';
interface VehicleSelectionProps {
  onBack: () => void;
  onConfirm: () => void;
}
export function VehicleSelection({
  onBack,
  onConfirm
}: VehicleSelectionProps) {
  const [selectedRide, setSelectedRide] = useState<string>('comfort');
  const vehicles = [{
    id: 'eco',
    name: 'Eco',
    description: 'Budget friendly',
    price: '2.50',
    time: '4 min',
    icon: Leaf,
    color: 'text-green-600',
    bg: 'bg-green-50',
    accent: 'border-green-500'
  }, {
    id: 'comfort',
    name: 'Comfort',
    description: 'Extra legroom',
    price: '4.00',
    time: '3 min',
    icon: Car,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    accent: 'border-blue-500'
  }, {
    id: 'boda',
    name: 'Boda',
    description: 'Motorcycle',
    price: '1.50',
    time: '2 min',
    icon: Bike,
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    accent: 'border-orange-500'
  }];
  return <motion.div initial={{
    x: '100%'
  }} animate={{
    x: 0
  }} exit={{
    x: '100%'
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 200
  }} className="flex h-full flex-col bg-slate-50" data-id="element-204">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-4 bg-slate-50 px-6 py-4" data-id="element-205">
        <button onClick={onBack} className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200" data-id="element-206">
          <ArrowLeft className="h-5 w-5 text-slate-700" data-id="element-207" />
        </button>
        <h1 className="text-lg font-bold text-slate-900" data-id="element-208">Choose your ride</h1>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pb-24" data-id="element-209">
        {/* Route Summary */}
        <div className="mb-6 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-100" data-id="element-210">
          <div className="relative flex flex-col gap-6 pl-2" data-id="element-211">
            {/* Connecting Line */}
            <div className="absolute left-[11px] top-3 bottom-3 w-0.5 bg-slate-200" data-id="element-212"></div>

            <div className="relative flex items-center gap-4" data-id="element-213">
              <div className="relative z-10 h-6 w-6 rounded-full border-4 border-white bg-blue-500 shadow-sm" data-id="element-214"></div>
              <div data-id="element-215">
                <p className="text-xs font-medium text-slate-400" data-id="element-216">Pickup</p>
                <p className="text-sm font-semibold text-slate-900" data-id="element-217">
                  Current Location
                </p>
              </div>
            </div>

            <div className="relative flex items-center gap-4" data-id="element-218">
              <div className="relative z-10 flex h-6 w-6 items-center justify-center rounded-full border-4 border-white bg-slate-900 shadow-sm" data-id="element-219">
                <div className="h-1.5 w-1.5 rounded-full bg-white" data-id="element-220"></div>
              </div>
              <div data-id="element-221">
                <p className="text-xs font-medium text-slate-400" data-id="element-222">Dropoff</p>
                <p className="text-sm font-semibold text-slate-900" data-id="element-223">
                  Central Market
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Vehicle Options */}
        <div className="flex flex-col gap-3" data-id="element-224">
          {vehicles.map((vehicle, index) => <motion.button key={vehicle.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} onClick={() => setSelectedRide(vehicle.id)} className={`relative flex items-center justify-between rounded-2xl border-2 p-4 transition-all ${selectedRide === vehicle.id ? `${vehicle.accent} bg-white shadow-md` : 'border-transparent bg-white shadow-sm ring-1 ring-slate-100 hover:bg-slate-50'}`} data-id="element-225">
              <div className="flex items-center gap-4" data-id="element-226">
                <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${vehicle.bg} ${vehicle.color}`} data-id="element-227">
                  <vehicle.icon className="h-6 w-6" data-id="element-228" />
                </div>
                <div className="text-left" data-id="element-229">
                  <p className="font-bold text-slate-900" data-id="element-230">{vehicle.name}</p>
                  <p className="text-xs text-slate-500" data-id="element-231">
                    {vehicle.description}
                  </p>
                </div>
              </div>
              <div className="text-right" data-id="element-232">
                <p className="font-bold text-slate-900" data-id="element-233">
                  {vehicle.price}{' '}
                  <span className="text-xs font-normal text-slate-500" data-id="element-234">
                    cUSD
                  </span>
                </p>
                <p className="text-xs font-medium text-slate-400" data-id="element-235">
                  {vehicle.time}
                </p>
              </div>
            </motion.button>)}
        </div>
      </div>

      {/* Bottom Action */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-slate-100 bg-white p-6 pb-8" data-id="element-236">
        <motion.button whileTap={{
        scale: 0.98
      }} onClick={onConfirm} className="flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 py-4 font-bold text-white shadow-lg shadow-blue-200 transition-colors hover:bg-blue-700" data-id="element-237">
          Confirm Ride
        </motion.button>
      </div>
    </motion.div>;
}